/**
 * Two-view triangulation — recovering SLAM-unit depth for tracked pixels.
 *
 * ## Why this exists
 *
 * The obvious source of depth — camera height above `findPlane()`'s floor —
 * rarely arrived: `metersPerUnit` was null for whole captures. (Since
 * 2026-10-04 the main cause is known to have been the floor-normal extraction
 * in AlvaARBackend reading the wrong matrix axis, which rejected ~97% of floors
 * as walls — see `planeFromAlvaMatrix`. Even fixed, the floor needs 8 agreeing
 * observations ~25 frames apart.) And AlvaAR will not help
 * directly: `getFramePoints()` returns 2D pixels only, never the 3D map
 * positions it holds internally.
 *
 * So we reconstruct depth ourselves. Given the same feature seen from two camera
 * poses, its distance follows from geometry alone. Crucially the poses come from
 * SLAM, so the result is in SLAM units — available whenever tracking works, with
 * no floor involved.
 *
 * ⚠️ This used to also feed `DepthScale`, which divided this SLAM-unit depth by
 * a server-supplied metric depth (`/api/depth`) to get true `metersPerUnit`.
 * Removed 2026-08-26 along with that whole feature — `scaleFromDepths()` and
 * its tests are gone; `triangulateOne()` below is now used only to feed
 * `SceneDepth`, which never converts to metres at all (see SceneDepth.ts for
 * why scale-free placement is what actually carries the piece).
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface Quat {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** A feature seen in two frames, in normalised image coords (0..1, y down). */
export interface Correspondence {
  u1: number;
  v1: number;
  u2: number;
  v2: number;
}

export interface TriangulationView {
  position: Vec3;
  orientation: Quat;
}

export interface TriangulatedPoint {
  /** Distance from view 1's camera centre, in the units of the given poses. */
  depth: number;
  /** How close the two rays came, relative to depth. Small = trustworthy. */
  residual: number;
  u1: number;
  v1: number;
  /**
   * The point itself, in world space — the midpoint estimate the depth was
   * derived from.
   *
   * ⚠️ This was always computed and then discarded; only the scalar `depth`
   * escaped. That discard is why placement had nothing to work with beyond a
   * single median: the function's whole output is a sparse 3D map of the room,
   * and the caller was being handed one number per point instead of the point.
   * See SceneDepth's cloud.
   */
  world: Vec3;
}

function rotate(q: Quat, v: Vec3): Vec3 {
  const tx = 2 * (q.y * v.z - q.z * v.y);
  const ty = 2 * (q.z * v.x - q.x * v.z);
  const tz = 2 * (q.x * v.y - q.y * v.x);
  return {
    x: v.x + q.w * tx + (q.y * tz - q.z * ty),
    y: v.y + q.w * ty + (q.z * tx - q.x * tz),
    z: v.z + q.w * tz + (q.x * ty - q.y * tx),
  };
}

const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;
const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const len = (a: Vec3) => Math.hypot(a.x, a.y, a.z);

/**
 * Camera-space ray for a normalised image coordinate.
 *
 * Pinhole, matching the rest of the stack: +x right, +y up, looking down -z.
 * Image v runs downward, hence the negation. `tanHalfX/Y` come from the
 * calibrated FOV (see spatial/cameraFov.ts) — the same numbers the renderer uses,
 * so a projection error would show up consistently rather than only here.
 */
export function rayForPixel(u: number, v: number, tanHalfX: number, tanHalfY: number): Vec3 {
  return {
    x: (u * 2 - 1) * tanHalfX,
    y: -(v * 2 - 1) * tanHalfY,
    z: -1,
  };
}

/**
 * Depth of one correspondence, by closest approach of the two view rays.
 *
 * Exact intersection is not expected: pixel noise and pose error leave the rays
 * skew. The midpoint of their closest approach is the standard estimate, and the
 * gap between them is a free quality signal — a large gap means the match was
 * wrong or the pose moved, and the caller can discard it.
 *
 * Returns null when the geometry cannot answer: rays near-parallel (too little
 * baseline to triangulate — the same degeneracy that makes rotation-only motion
 * useless to SLAM), or a point behind the camera.
 */
export function triangulateOne(
  c: Correspondence,
  v1: TriangulationView,
  v2: TriangulationView,
  tanHalfX: number,
  tanHalfY: number,
): TriangulatedPoint | null {
  const d1 = rotate(v1.orientation, rayForPixel(c.u1, c.v1, tanHalfX, tanHalfY));
  const d2 = rotate(v2.orientation, rayForPixel(c.u2, c.v2, tanHalfX, tanHalfY));
  const w0 = sub(v1.position, v2.position);

  const a = dot(d1, d1);
  const b = dot(d1, d2);
  const cc = dot(d2, d2);
  const d = dot(d1, w0);
  const e = dot(d2, w0);

  const denom = a * cc - b * b;
  // Near-zero denominator = near-parallel rays = insufficient baseline.
  if (!Number.isFinite(denom) || Math.abs(denom) < 1e-9) return null;

  const s = (b * e - cc * d) / denom; // along ray 1
  const t = (a * e - b * d) / denom; // along ray 2
  if (!(s > 0) || !(t > 0)) return null; // behind one of the cameras

  const p1: Vec3 = {
    x: v1.position.x + d1.x * s,
    y: v1.position.y + d1.y * s,
    z: v1.position.z + d1.z * s,
  };
  const p2: Vec3 = {
    x: v2.position.x + d2.x * t,
    y: v2.position.y + d2.y * t,
    z: v2.position.z + d2.z * t,
  };

  const depth = len(sub(p1, v1.position));
  if (!(depth > 0) || !Number.isFinite(depth)) return null;

  // Midpoint of closest approach, not p1 — p1 is the point on ray 1 alone,
  // which is biased toward view 1 whenever the rays are skew.
  const world: Vec3 = {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
    z: (p1.z + p2.z) / 2,
  };

  return { depth, residual: len(sub(p1, p2)) / depth, u1: c.u1, v1: c.v1, world };
}
